/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { forwardRef, useCallback, useEffect, useId, useState } from "react";
import { cn } from "../../lib/utils";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  minPlaceholder?: string;
  autoGrow?: boolean;
  showCount?: boolean;
}

const MOBILE_MAX_WIDTH = 768;
const NEAR_LIMIT_RATIO = 0.8;

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      id,
      error,
      value,
      onChange,
      minPlaceholder,
      placeholder,
      autoGrow = false,
      showCount = false,
      maxLength,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [textareaElement, setTextareaElement] =
      useState<HTMLTextAreaElement | null>(null);
    const generatedId = useId();
    const textareaId = id ?? generatedId;
    const errorId = `${textareaId}-error`;
    const hasError = !!error;

    const callbackRef = useCallback(
      (node: HTMLTextAreaElement | null) => {
        setTextareaElement(node);
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    useEffect(() => {
      const checkMobile = () => setIsMobile(window.innerWidth < MOBILE_MAX_WIDTH);
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }, []);

    useEffect(() => {
      if (autoGrow && textareaElement) {
        textareaElement.style.height = "auto";
        textareaElement.style.height = `${textareaElement.scrollHeight}px`;
        textareaElement.style.overflowY =
          textareaElement.scrollHeight > textareaElement.clientHeight
            ? "auto"
            : "hidden";
      }
    }, [value, autoGrow, textareaElement]);

    const effectivePlaceholder =
      isMobile && minPlaceholder ? minPlaceholder : placeholder;

    const currentLength = typeof value === "string" ? value.length : 0;
    const limit = typeof maxLength === "number" ? maxLength : undefined;
    const renderCount = showCount && limit !== undefined;
    const atOrOverLimit = limit !== undefined && renderCount && currentLength >= limit;
    const nearLimit =
      limit !== undefined &&
      renderCount &&
      !atOrOverLimit &&
      currentLength >= Math.floor(limit * NEAR_LIMIT_RATIO);
    const countClass = atOrOverLimit
      ? "text-error font-semibold"
      : nearLimit
        ? "text-warning"
        : "text-text-tertiary";

    const limitMessage = atOrOverLimit && !error ? "Max characters reached" : null;
    const leftMessage = error || limitMessage;

    const bottomRow =
      leftMessage || renderCount ? (
        <div className="flex items-baseline justify-between gap-3 mt-1">
          {leftMessage && (
            <p id={errorId} className="text-sm text-error font-medium min-w-0 break-words">
              {leftMessage}
            </p>
          )}
          {renderCount && (
            <span
              className={cn(
                "text-xs tabular-nums shrink-0 ml-auto",
                countClass
              )}
              aria-live="polite"
            >
              {currentLength}/{limit}
            </span>
          )}
        </div>
      ) : null;

    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={textareaId}
            className={cn(
              "block text-sm font-medium transition-colors duration-200",
              hasError
                ? "text-error"
                : isFocused
                  ? "text-interactive"
                  : "text-text-secondary"
            )}
          >
            {label}
          </label>
        )}

        <textarea
          id={textareaId}
          aria-invalid={hasError || undefined}
          aria-describedby={leftMessage ? errorId : undefined}
          className={cn(
            "flex w-full rounded-xl border bg-bg-elevated px-3 py-2 text-base transition-all duration-200",
            "focus:outline-none text-text-primary",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "placeholder:text-text-tertiary",
            autoGrow
              ? "min-h-11 resize-none overflow-hidden max-h-56 md:max-h-80"
              : "min-h-[120px] resize-y",
            hasError
              ? "border-error focus:border-error focus:ring-2 focus:ring-error/20"
              : "border-border hover:border-border-hover focus:border-interactive focus:ring-2 focus:ring-interactive/20",
            className
          )}
          ref={callbackRef}
          value={value}
          placeholder={effectivePlaceholder}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={onChange}
          maxLength={maxLength}
          {...props}
        />

        {bottomRow}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export { Textarea };
